import { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  FlaskConical,
  Loader2,
  PhoneCall,
  Plus,
  Send,
  Settings2,
  Trash2,
} from 'lucide-react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { AutomationTemplateConfigureBar } from '@/components/settings/AutomationTemplateConfigureBar'
import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi'
import {
  buildPostCallVariableMapping,
  normalizePostCallMappingForApi,
  normalizePostCallMappingFromApi,
  type PostCallVariableMappingEntry,
} from '@/components/settings/postCallTemplateMapping'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import { log } from '@/lib/logger'
import { useWhatsAppStatusQuery } from '@/queries'
import { PostCallTemplateDecisionsPanel } from '@/components/settings/PostCallTemplateDecisionsPanel'
import {
  postCallTemplateService,
  type PostCallMetaTemplate,
  type PostCallTemplateOption,
  type PostCallTemplatePolicy,
  type PostCallVariable,
  type PostCallVariableDefinition,
} from '@/services/postCallTemplateService'
import { whatsappService, type WABA } from '@/services/whatsappService'

function isNotFoundError(err: unknown): boolean {
  return err instanceof Error && /not found|404/i.test(err.message)
}

type OptionDraft = {
  waba_id: string
  template_key: string
  template_name: string
  template_language: string
  body_parameter_count: number
  variable_mapping: PostCallVariableMappingEntry[]
  required_signals: string[]
  purpose_plain_text: string
  when_to_use_plain_text: string
  priority: number
  enabled: boolean
}

function emptyDraft(): OptionDraft {
  return {
    waba_id: '',
    template_key: '',
    template_name: '',
    template_language: 'es_ES',
    body_parameter_count: 0,
    variable_mapping: [],
    required_signals: [],
    purpose_plain_text: '',
    when_to_use_plain_text: '',
    priority: 100,
    enabled: true,
  }
}

function extractBodyText(template: PostCallMetaTemplate | undefined): string {
  const body = template?.components?.find((c) => c.type?.toUpperCase() === 'BODY')
  return body?.text || ''
}

function describeWabaOption(waba: WABA): string {
  return [waba.name || waba.id, waba.display_phone_number]
    .filter(Boolean)
    .join(' · ')
}

function isSendableWaba(waba: WABA): boolean {
  return Boolean(waba.phone_number_id && String(waba.phone_number_id).trim())
}

export function PostCallTemplatesSettingsCard() {
  const { lang = 'en' } = useParams()
  const { data: whatsappStatus, isLoading: whatsappStatusLoading } = useWhatsAppStatusQuery({
    enabled: META_INTEGRATION_ENABLED,
  })
  const whatsappConnected = Boolean(whatsappStatus?.connected)

  const [policy, setPolicy] = useState<PostCallTemplatePolicy | null>(null)
  const [options, setOptions] = useState<PostCallTemplateOption[]>([])
  const [variables, setVariables] = useState<PostCallVariable[]>([])
  const [variableDefinitions, setVariableDefinitions] = useState<PostCallVariableDefinition[]>([])
  const [newVariableKey, setNewVariableKey] = useState('')
  const [newVariableLabel, setNewVariableLabel] = useState('')
  const [newVariableUseInDecision, setNewVariableUseInDecision] = useState(true)
  const [savingVariable, setSavingVariable] = useState(false)
  const [variableSearch, setVariableSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [apiUnavailable, setApiUnavailable] = useState(false)
  const [savingPolicy, setSavingPolicy] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingOption, setEditingOption] = useState<PostCallTemplateOption | null>(null)
  const [draft, setDraft] = useState<OptionDraft>(emptyDraft())
  const [wabas, setWabas] = useState<WABA[]>([])
  const [metaTemplates, setMetaTemplates] = useState<PostCallMetaTemplate[]>([])
  const [dialogLoading, setDialogLoading] = useState(false)
  const [savingOption, setSavingOption] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [testSendOption, setTestSendOption] = useState<PostCallTemplateOption | null>(null)
  const [testPhone, setTestPhone] = useState('+34600111222')
  const [testSendConfirmed, setTestSendConfirmed] = useState(false)
  const [testSendLoading, setTestSendLoading] = useState(false)
  const [decisionLogsOpen, setDecisionLogsOpen] = useState(false)

  const canConfigureTemplates = META_INTEGRATION_ENABLED && whatsappConnected && !apiUnavailable
  const sendableWabas = useMemo(() => wabas.filter(isSendableWaba), [wabas])

  const selectedTemplate = useMemo(
    () => metaTemplates.find((t) => `${t.name}:${t.language}` === draft.template_key),
    [metaTemplates, draft.template_key],
  )
  const decisionSignalOptions = useMemo(
    () =>
      variableDefinitions
        .filter((variable) => variable.enabled && variable.use_in_decision)
        .map((variable) => ({ key: variable.key, label: variable.label })),
    [variableDefinitions],
  )
  const filteredVariableDefinitions = useMemo(() => {
    const query = variableSearch.trim().toLowerCase()
    if (!query) return variableDefinitions
    return variableDefinitions.filter((item) => {
      const key = item.key.toLowerCase()
      const label = item.label.toLowerCase()
      return key.includes(query) || label.includes(query)
    })
  }, [variableDefinitions, variableSearch])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    setApiUnavailable(false)
    try {
      const [policyData, optionsData, variablesData, definitionsData] = await Promise.all([
        postCallTemplateService.getPolicy(),
        postCallTemplateService.listOptions(),
        postCallTemplateService.getVariables(),
        postCallTemplateService.listVariableDefinitions(),
      ])
      setPolicy(policyData)
      setOptions(optionsData)
      setVariables(variablesData.variables)
      setVariableDefinitions(definitionsData)
    } catch (err) {
      if (isNotFoundError(err)) {
        setPolicy(null)
        setOptions([])
        setVariables([])
        setVariableDefinitions([])
        setApiUnavailable(true)
        return
      }
      const message = err instanceof Error ? err.message : 'Failed to load post-call templates'
      log.error('Post-call templates load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  const loadMetaTemplates = async (wabaId: string) => {
    setMetaTemplates([])
    if (!wabaId) return
    const response = await postCallTemplateService.getMetaTemplates(wabaId)
    setMetaTemplates(response.data || [])
  }

  const openCreateDialog = async () => {
    if (apiUnavailable) {
      toast.message('Post-call template API is not available in this local backend.')
      return
    }
    if (!canConfigureTemplates) {
      toast.message('WhatsApp is connected only in production/QA, so local template selection is disabled.')
      return
    }
    setEditingOption(null)
    setDraft(emptyDraft())
    setPreviewText(null)
    setDialogError(null)
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const eligibleWabas = availableWabas.filter(isSendableWaba)
      const wabaId = eligibleWabas[0]?.id || ''
      setDraft((prev) => ({ ...prev, waba_id: wabaId }))
      if (wabaId) await loadMetaTemplates(wabaId)
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to open dialog')
    } finally {
      setDialogLoading(false)
    }
  }

  const openEditDialog = async (option: PostCallTemplateOption) => {
    setEditingOption(option)
    setPreviewText(null)
    setDialogError(null)
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      await loadMetaTemplates(option.waba_id)
      setDraft({
        waba_id: option.waba_id,
        template_key: `${option.template_name}:${option.template_language}`,
        template_name: option.template_name,
        template_language: option.template_language,
        body_parameter_count: option.body_parameter_count,
        variable_mapping: normalizePostCallMappingFromApi(option.variable_mapping),
        required_signals: option.required_signals || [],
        purpose_plain_text: option.purpose_plain_text || '',
        when_to_use_plain_text: option.when_to_use_plain_text || '',
        priority: option.priority,
        enabled: option.enabled,
      })
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to open dialog')
    } finally {
      setDialogLoading(false)
    }
  }

  const handleWabaChange = async (wabaId: string) => {
    setDraft((prev) => ({
      ...prev,
      waba_id: wabaId,
      template_key: '',
      template_name: '',
      body_parameter_count: 0,
      variable_mapping: [],
    }))
    setPreviewText(null)
    try {
      await loadMetaTemplates(wabaId)
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to load templates')
    }
  }

  const handleTemplateChange = (templateKey: string) => {
    const template = metaTemplates.find((t) => `${t.name}:${t.language}` === templateKey)
    const count = template?.body_parameter_count || 0
    setDraft((prev) => ({
      ...prev,
      template_key: templateKey,
      template_name: template?.name || '',
      template_language: template?.language || 'es_ES',
      body_parameter_count: count,
      variable_mapping: buildPostCallVariableMapping(count, prev.variable_mapping),
    }))
    setPreviewText(null)
  }

  const handleMappingChange = (position: number, sourceKey: string) => {
    setDraft((prev) => ({
      ...prev,
      variable_mapping: prev.variable_mapping.map((item) =>
        item.position === position ? { ...item, source_type: 'variable', source_key: sourceKey } : item,
      ),
    }))
    setPreviewText(null)
  }

  const handlePolicyToggle = async (field: 'enabled' | 'require_whatsapp_opt_in', value: boolean) => {
    if (!policy) return
    setSavingPolicy(true)
    try {
      const updated = await postCallTemplateService.patchPolicy({ [field]: value })
      setPolicy(updated)
      toast.success(field === 'enabled' ? 'Post-call automation updated' : 'Opt-in requirement updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo actualizar la política')
    } finally {
      setSavingPolicy(false)
    }
  }

  const handlePreview = async () => {
    setPreviewLoading(true)
    setDialogError(null)
    try {
      const payload = editingOption
        ? { option_id: editingOption.id }
        : {
            waba_id: draft.waba_id,
            template_name: draft.template_name,
            template_language: draft.template_language,
            variable_mapping: normalizePostCallMappingForApi(draft.variable_mapping),
          }
      const result = await postCallTemplateService.preview(payload)
      const bodyParts = result.components?.body?.map((part) => part.text).join(' | ') || '(empty)'
      setPreviewText(
        result.missing_variables.length
          ? `Faltan: ${result.missing_variables.join(', ')}`
          : `Vista previa: ${bodyParts}`,
      )
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Falló la vista previa')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSaveOption = async () => {
    if (!draft.waba_id || !draft.template_name) {
      setDialogError('Selecciona una cuenta de WhatsApp y una plantilla aprobada.')
      return
    }

    setSavingOption(true)
    setDialogError(null)
    try {
      const payload = {
        waba_id: draft.waba_id,
        template_name: draft.template_name,
        template_language: draft.template_language,
        variable_mapping: normalizePostCallMappingForApi(draft.variable_mapping),
        required_variables: [],
        required_signals: draft.required_signals,
        purpose_plain_text: draft.purpose_plain_text,
        when_to_use_plain_text: draft.when_to_use_plain_text,
        priority: draft.priority,
        enabled: draft.enabled,
      }

      if (editingOption) {
        await postCallTemplateService.updateOption(editingOption.id, payload)
        toast.success('Plantilla actualizada')
      } else {
        await postCallTemplateService.createOption(payload)
        toast.success('Plantilla creada')
      }

      setDialogOpen(false)
      await loadAll()
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'No se pudo guardar la plantilla')
    } finally {
      setSavingOption(false)
    }
  }

  const handleDeleteOption = async (option: PostCallTemplateOption) => {
    if (!window.confirm(`¿Eliminar esta plantilla de la lista?`)) return
    try {
      await postCallTemplateService.deleteOption(option.id)
      toast.success('Plantilla eliminada')
      await loadAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar la plantilla')
    }
  }

  const handleCreateVariable = async () => {
    if (!newVariableKey.trim() || !newVariableLabel.trim()) {
      toast.error('La clave y el nombre son obligatorios')
      return
    }
    setSavingVariable(true)
    try {
      await postCallTemplateService.createVariableDefinition({
        key: newVariableKey,
        label: newVariableLabel,
        enabled: true,
        use_in_decision: newVariableUseInDecision,
      })
      setNewVariableKey('')
      setNewVariableLabel('')
      toast.success('Variable creada')
      await loadAll()
    } catch (err) {
      if (isNotFoundError(err)) {
        setApiUnavailable(true)
        toast.message('La API de post-llamada no está disponible en este backend local (404).')
        return
      }
      toast.error(err instanceof Error ? err.message : 'No se pudo crear la variable')
    } finally {
      setSavingVariable(false)
    }
  }

  const handleDeleteVariable = async (item: PostCallVariableDefinition) => {
    if (item.source === 'system') {
      toast.error('Las variables del sistema no se pueden eliminar. Puedes desactivarlas o quitarles "Decisión".')
      return
    }
    if (!window.confirm(`¿Eliminar variable "${item.label}" (${item.key})?`)) return
    try {
      await postCallTemplateService.deleteVariableDefinition(item.id)
      toast.success('Variable eliminada')
      await loadAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar la variable')
    }
  }

  const handleToggleVariableDecision = async (item: PostCallVariableDefinition, enabled: boolean) => {
    try {
      await postCallTemplateService.updateVariableDefinition(item.id, { use_in_decision: enabled })
      await loadAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo actualizar la variable')
    }
  }

  const toggleOptionEnabled = async (option: PostCallTemplateOption) => {
    try {
      await postCallTemplateService.updateOption(option.id, { enabled: !option.enabled })
      await loadAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo actualizar la plantilla')
    }
  }

  const openTestSendDialog = (option: PostCallTemplateOption) => {
    setTestSendOption(option)
    setTestPhone('+34600111222')
    setTestSendConfirmed(false)
  }

  const handleTestSend = async () => {
    if (!testSendOption) return
    if (!testSendConfirmed) {
      toast.error('Confirma el envío de prueba antes de enviar.')
      return
    }
    setTestSendLoading(true)
    try {
      const result = await postCallTemplateService.testSend(testSendOption.id, {
        phone_e164: testPhone,
        confirmed: true,
      })
      toast.success(`Envío de prueba en cola${result.message_id ? `: ${result.message_id}` : ''}`)
      setTestSendOption(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falló el envío de prueba')
    } finally {
      setTestSendLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando configuración post-llamada...
        </CardContent>
      </Card>
    )
  }

  return (
    <>
    <SeccionPlegable
      numero="03"
      etiqueta="Automatización post-llamada"
      titulo="WhatsApp después de la llamada"
      descripcion="Envía la plantilla correcta y revisa cada decisión."
      estado={
        <Badge variant="outline" className="ml-1 border-success/40 text-success">
          Listo
        </Badge>
      }
      abierta={isOpen}
      onAlternar={() => setIsOpen((prev) => !prev)}
    >
        <div className="grid gap-2 md:grid-cols-3">
          <div className="space-y-1">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              <PhoneCall className="h-4 w-4 text-primary" />
              1. Termina la llamada
            </div>
            <p className="text-xs text-muted-foreground">
              ElevenLabs envía el webhook con señales de la llamada y datos del inmueble.
            </p>
          </div>
          <div className="space-y-1">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              2. Se evalúan reglas
            </div>
            <p className="text-xs text-muted-foreground">
              El sistema valida consentimiento, reglas obligatorias y variables mapeadas antes de enviar.
            </p>
          </div>
          <div className="space-y-1">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              <FlaskConical className="h-4 w-4 text-primary" />
              3. Resultado auditado
            </div>
            <p className="text-xs text-muted-foreground">
              Cada envío, omisión, duplicado o error de Meta aparece en el log de decisiones.
            </p>
          </div>
        </div>

        <AutomationTemplateConfigureBar
          onAdd={() => void openCreateDialog()}
          disabled={!canConfigureTemplates}
        />

        <AutomationTemplateMetaAlerts
          lang={lang}
          metaIntegrationEnabled={META_INTEGRATION_ENABLED}
          whatsappConnected={whatsappConnected}
          whatsappStatusLoading={whatsappStatusLoading}
          serviceUnavailable={apiUnavailable}
        />

        {META_INTEGRATION_ENABLED && whatsappConnected ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className="border-success/40 text-success">
              WhatsApp conectado
            </Badge>
            {whatsappStatus?.metadata?.display_phone_number ? (
              <span>{whatsappStatus.metadata.display_phone_number}</span>
            ) : null}
            {whatsappStatus?.account_id ? (
              <span className="text-xs">WABA {whatsappStatus.account_id}</span>
            ) : null}
          </div>
        ) : null}
        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-4 space-y-2 p-4 md:grid-cols-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <Label htmlFor="post-call-enabled">Automatización activa</Label>
              <p className="text-xs text-muted-foreground">Activa o desactiva todos los envíos post-llamada.</p>
            </div>
            <Switch
              id="post-call-enabled"
              checked={policy?.enabled ?? false}
              disabled={savingPolicy || apiUnavailable}
              onCheckedChange={(value) => void handlePolicyToggle('enabled', value)}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <Label htmlFor="post-call-opt-in">Exigir consentimiento de WhatsApp</Label>
              <p className="text-xs text-muted-foreground">Si falta consentimiento, no se envía.</p>
            </div>
            <Switch
              id="post-call-opt-in"
              checked={policy?.require_whatsapp_opt_in ?? false}
              disabled={savingPolicy || apiUnavailable}
              onCheckedChange={(value) => void handlePolicyToggle('require_whatsapp_opt_in', value)}
            />
          </div>
        </div>

        <div className="space-y-2 space-y-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <Label>Variables después de la llamada</Label>
              <p className="text-xs text-muted-foreground">
                Crea variables con la misma clave que envía ElevenLabs.
              </p>
              <p className="text-xs text-muted-foreground">
                Para consentimiento, usa exactamente <code>whatsapp_opt_in</code> con valor <code>true</code> o <code>false</code>.
              </p>
            </div>
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="text-sm font-medium text-foreground">Crear variable</div>
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] xl:grid-cols-1">
                <Input placeholder="clave (ejemplo: whatsapp_opt_in o budget_range)" value={newVariableKey} onChange={(event) => setNewVariableKey(event.target.value)} />
                <Input placeholder="nombre visible" value={newVariableLabel} onChange={(event) => setNewVariableLabel(event.target.value)} />
                <Button type="button" className="w-full whitespace-normal break-words text-center" onClick={() => void handleCreateVariable()} disabled={savingVariable}>
                  {savingVariable ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Agregar
                </Button>
              </div>
              <Label className="flex items-start gap-2 text-xs text-muted-foreground">
                <Switch checked={newVariableUseInDecision} onCheckedChange={setNewVariableUseInDecision} />
                <span className="leading-relaxed break-words">
                  Actívala para usar esta variable en reglas de envío. Si exiges consentimiento, actívala para <code>whatsapp_opt_in</code>.
                </span>
              </Label>
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="text-sm font-medium text-foreground">Lista de variables</div>
              <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                <Input
                  placeholder="Buscar variables..."
                  value={variableSearch}
                  onChange={(event) => setVariableSearch(event.target.value)}
                />
                <div className="flex items-center justify-end text-xs text-muted-foreground">
                  {filteredVariableDefinitions.length} / {variableDefinitions.length}
                </div>
              </div>
              {variableDefinitions.length > 0 ? (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {filteredVariableDefinitions.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">{item.label}</div>
                        <div className="truncate text-xs text-muted-foreground">{item.key}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Switch checked={item.use_in_decision} onCheckedChange={(value) => void handleToggleVariableDecision(item, value)} />
                          Decisión
                        </Label>
                        {item.source !== 'system' ? (
                          <Button type="button" size="sm" variant="outline" onClick={() => void handleDeleteVariable(item)}><Trash2 className="h-4 w-4" /></Button>
                        ) : (
                          <Badge variant="outline" className="text-xs">Sistema</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredVariableDefinitions.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                      No hay variables con ese filtro.
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {options.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-background/70 px-4 py-8 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <PhoneCall className="h-5 w-5" />
            </div>
            <div className="text-sm font-medium text-foreground">Todavía no hay reglas de plantilla</div>
            <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
              {apiUnavailable
                ? 'La ruta API local no está disponible, así que todavía no se pueden cargar reglas guardadas.'
                : canConfigureTemplates
                  ? 'Agrega una plantilla aprobada y mapea sus variables para activar el flujo.'
                  : 'Cuando WhatsApp esté conectado, elige una plantilla aprobada y mapea sus variables.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plantilla</TableHead>
                <TableHead>Idioma</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {options.map((option) => (
                <TableRow key={option.id}>
                  <TableCell className="font-medium">{option.template_name}</TableCell>
                  <TableCell>{option.template_language}</TableCell>
                  <TableCell>{option.priority}</TableCell>
                  <TableCell>
                    <Badge variant={option.enabled ? 'default' : 'secondary'}>
                      {option.enabled ? 'Activa' : 'Desactivada'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => void toggleOptionEnabled(option)}>
                        {option.enabled ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canConfigureTemplates}
                        onClick={() => openTestSendDialog(option)}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canConfigureTemplates}
                        onClick={() => void openEditDialog(option)}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => void handleDeleteOption(option)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}

        {/* Era un `Button` con su propio relleno y su propio chevron, es decir
            una cabecera desplegable escrita a mano por septima vez. Es la misma
            pieza, en su forma plana: dentro de una seccion no cabe otra
            tarjeta. */}
        <SeccionPlegable
          plana
          titulo="Decision log"
          abierta={decisionLogsOpen}
          onAlternar={() => setDecisionLogsOpen((value) => !value)}
        >
          <PostCallTemplateDecisionsPanel apiUnavailable={apiUnavailable} isOpen={decisionLogsOpen} />
        </SeccionPlegable>
    </SeccionPlegable>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOption
                ? AUTOMATION_TEMPLATE_UI.editTemplateButton
                : AUTOMATION_TEMPLATE_UI.addTemplateButton}
            </DialogTitle>
          </DialogHeader>

          {dialogLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando plantillas de Meta...
            </div>
          ) : (
            <div className="space-y-4">
              {dialogError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label>Cuenta de WhatsApp</Label>
                <Select value={draft.waba_id} onValueChange={(value) => void handleWabaChange(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar WABA" />
                  </SelectTrigger>
                  <SelectContent>
                    {sendableWabas.map((waba) => (
                      <SelectItem key={waba.id} value={waba.id}>
                        {describeWabaOption(waba)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plantilla aprobada de Meta</Label>
                <Select value={draft.template_key} onValueChange={handleTemplateChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {metaTemplates.map((template) => (
                      <SelectItem key={`${template.name}:${template.language}`} value={`${template.name}:${template.language}`}>
                        {template.name} ({template.language}) — {template.body_parameter_count} vars
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplate ? (
                  <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                    {extractBodyText(selectedTemplate)}
                  </p>
                ) : null}
              </div>

              {draft.body_parameter_count > 0 ? (
                <div className="space-y-2">
                  <Label>Mapa de variables</Label>
                  {draft.variable_mapping.map((mapping) => (
                    <div key={mapping.position} className="grid grid-cols-[80px_1fr] items-center gap-2">
                      <span className="text-sm font-medium">{`{{${mapping.position}}}`}</span>
                      <Select
                        value={mapping.source_key || undefined}
                        onValueChange={(value) => handleMappingChange(mapping.position, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar variable del sistema" />
                        </SelectTrigger>
                        <SelectContent>
                          {variables.map((variable) => (
                            <SelectItem key={variable.key} value={variable.key}>
                              {variable.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label>Variables obligatorias</Label>
                <p className="text-xs text-muted-foreground">
                  Solo variables con "Decisión" activa. Deben tener la misma clave en ElevenLabs.
                </p>
                {decisionSignalOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay variables de decisión. Activa "Decisión" en al menos una variable.</p>
                ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {decisionSignalOptions.map((signal) => (
                    <Label key={signal.key} className="flex items-center gap-2 font-normal">
                      <Checkbox
                        checked={draft.required_signals.includes(signal.key)}
                        onCheckedChange={(valor) => {
                          const event = { target: { checked: valor === true } }
                          setDraft((prev) => ({
                            ...prev,
                            required_signals: event.target.checked
                              ? [...prev.required_signals, signal.key]
                              : prev.required_signals.filter((item) => item !== signal.key),
                          }))
                        }}
                      />
                      {signal.label}
                    </Label>
                  ))}
                </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Input
                    type="number"
                    value={draft.priority}
                    onChange={(event) => setDraft((prev) => ({ ...prev, priority: Number(event.target.value) || 0 }))}
                  />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch
                    checked={draft.enabled}
                    onCheckedChange={(value) => setDraft((prev) => ({ ...prev, enabled: value }))}
                  />
                  <Label>Opción activa</Label>
                </div>
              </div>

              {previewText ? (
                <Alert>
                  <AlertDescription>{previewText}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={previewLoading} onClick={() => void handlePreview()}>
              {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
              Vista previa
            </Button>
            <Button type="button" disabled={savingOption || dialogLoading} onClick={() => void handleSaveOption()}>
              {savingOption ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testSendOption !== null} onOpenChange={(open) => !open && setTestSendOption(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envío de prueba de plantilla post-llamada</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Envía <strong>{testSendOption?.template_name}</strong> con datos de ejemplo. No afecta el dedupe real.
            </p>
            <div className="space-y-2">
              <Label htmlFor="test-send-phone">Teléfono de prueba (E.164)</Label>
              <Input
                id="test-send-phone"
                value={testPhone}
                onChange={(event) => setTestPhone(event.target.value)}
                placeholder="+34600111222"
              />
            </div>
            <Label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={testSendConfirmed}
                onCheckedChange={(valor) => setTestSendConfirmed(valor === true)}
              />
              <span>Confirmo que este WhatsApp de prueba puede enviarse al número anterior.</span>
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTestSendOption(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={testSendLoading || !testSendConfirmed}
              onClick={() => void handleTestSend()}
            >
              {testSendLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Enviar prueba
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
